import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC, MouseEvent, MouseEventHandler } from 'react';
import { useState } from 'react';

import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import { PrimaryBuyFlow } from '#src/components/primaryBuy/PrimaryBuyBuyField/PrimaryOfferBuyFieldWrapper/PrimaryBuyFlow/index.tsx';
import type { PrimaryBuyConfirmationOptions } from '#src/contexts/buyingConfirmation/index.tsx';

import type { WebPrimaryOfferBuyFieldWrapper_anyCard } from './__generated__/index.graphql.ts';

export interface Props {
  card: WebPrimaryOfferBuyFieldWrapper_anyCard;
  primaryBuyConfirmationOptions?: PrimaryBuyConfirmationOptions;
  onSuccess?: () => void;
  onBuyConfirmationClose?: () => void;
  origin: ClickInstantBuy_Flow;
  gameSlug?: string;
  showBuyConfirmation?: boolean;
  onlyCreditCards?: boolean;
  applyConversionCredits?: boolean;
  composeSessionId?: string;
  children: FC<
    React.PropsWithChildren<{
      onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
      loading: boolean;
      disabled?: boolean;
    }>
  >;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  rosterId?: string;
}

export const WebPrimaryOfferBuyFieldWrapper = ({
  children,
  card,
  primaryBuyConfirmationOptions,
  onSuccess,
  onBuyConfirmationClose,
  origin,
  gameSlug,
  showBuyConfirmation = true,
  onlyCreditCards = false,
  applyConversionCredits = false,
  onClick,
  composeSessionId = '',
  rosterId,
}: Props) => {
  const {
    isMarketAuthorizedIosApp,
    postMessage,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
  } = useIsMobileApp();
  const canOpenPaymentBox =
    isMarketAuthorizedIosApp && canOpenCartOpenPaymentBoxAndCloseCurrentWebview;

  const primaryOffer = card.livePrimaryOffer;
  const { id, price, signedAmount } = primaryOffer || {};
  const { toMonetaryAmount } = useMonetaryAmount();
  const [paymentStarted, setPaymentStarted] = useState(false);

  const track = useEvents();
  const loggedTogglePaymentStarted = useLoggedCallback<boolean>(b => {
    if (isMarketAuthorizedIosApp && canOpenPaymentBox) {
      postMessage('openPaymentBox');
    }
    setPaymentStarted(b);
  });

  const priceMonetaryAmount = toMonetaryAmount(price);

  if (!price || !signedAmount || !id) return null;

  const onBuyButtonClick = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    track('Click Instant Buy', {
      offerId: id,
      cardSlug: card.slug,
      ethAmount: priceMonetaryAmount.eth,
      eurAmount: priceMonetaryAmount.eur,
      gameSlug,
      origin,
      rosterId,
      scarcity: card.rarityTyped,
      sport: card.sport,
      composeSessionId,
    });
    loggedTogglePaymentStarted(true);
    onClick?.(e);
  };

  return (
    <>
      {children({
        loading: paymentStarted,
        onClick: onBuyButtonClick,
      })}
      {!!paymentStarted && (
        <PrimaryBuyFlow
          offerId={id}
          onSuccess={onSuccess}
          onClose={() => setPaymentStarted(false)}
          primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
          onBuyConfirmationClose={onBuyConfirmationClose}
          onlyCreditCards={onlyCreditCards}
          showBuyConfirmation={showBuyConfirmation}
          applyConversionCredits={applyConversionCredits}
        />
      )}
    </>
  );
};

WebPrimaryOfferBuyFieldWrapper.fragments = {
  anyCard: gql`
    fragment WebPrimaryOfferBuyFieldWrapper_anyCard on AnyCardInterface {
      slug
      sport
      rarityTyped
      livePrimaryOffer {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        signedAmount
        instantBuyCampaign {
          id
          soldOut
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<WebPrimaryOfferBuyFieldWrapper_anyCard>,
};
